import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { DraftGuardContext } from "../features/processAuthoring/draft/draftGuardContext";
import { useNavigate } from "react-router";
import { useSession } from "../session/sessionContext";
import { isAllowed } from "../api/access/decisions";
import { sessionDecision } from "../session/sessionMachine";
import { appPaths } from "../shared/routing/appPaths";
import { ADMINISTRATION_PAGES } from "../features/administration";
import { useConnectorSetupGlow } from "../features/connectors/setup/useConnectorSetup";
import { PROFILE_PAGES } from "../features/profile/profilePages";
import PersonAvatar from "../ui/avatars/PersonAvatar";
import MenuButton, { type MenuSection } from "../ui/overlays/MenuButton";
import { classNames } from "../ui/styles/classNames";

/**
 * The account menu (F14, option A; `.local/ux-review/profile-menu/report.md` PM-3, PM-6):
 * the avatar is a menu button that opens and closes on click or tap, never on hover;
 * Escape and a click outside close it (`MenuButton`). Below whose account it is: the person's
 * own pages, as the profile tabs name them (`PROFILE_PAGES`, F79), Administration for anyone
 * holding one of its installation permissions, and Sign out.
 */
export default function AccountMenu({ className }: { className?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { session, signOut } = useSession();
  const { requestClose } = useContext(DraftGuardContext);
  // F32: while the setup guide waits for this PC's connector, its entry and the avatar glow.
  const glow = useConnectorSetupGlow();
  if (session.status !== "signedIn") return null;
  const { account } = session.session;
  const go = (path: string) => () => { void navigate(path); };
  // Administration opens on the first of its pages the person may use (GS-2).
  const administration = ADMINISTRATION_PAGES.find((page) => isAllowed(sessionDecision(session, page.action)));
  const sections: MenuSection[] = [
    {
      id: "personal",
      items: PROFILE_PAGES.map((page) => ({
        id: page.id,
        label: t(`profile.pages.${page.id}`),
        action: { kind: "select", onSelect: go(page.path) },
        className: glow && page.id === "connectors" ? "app-connector-glow" : undefined,
      })),
    },
    {
      id: "administration",
      items: administration === undefined
        ? []
        : [{ id: "administration", label: t("administration.title"), action: { kind: "select", onSelect: go(administration.path) } }],
    },
    {
      id: "session",
      items: [{
        id: "signOut",
        label: t("profile.logout"),
        // Unsaved drafts are offered for saving first, as when leaving a page. If signing out
        // fails, the profile page's own Sign out shows why and can try again.
        action: { kind: "select", onSelect: () => { requestClose(() => { void signOut().then(() => navigate(appPaths.login()), () => navigate(appPaths.profile())); }); } },
      }],
    },
  ];
  return (
    <MenuButton
      className={className}
      align="right"
      width="md"
      sections={sections}
      header={
        <div className="flex items-center gap-3 px-3 py-2.5">
          <PersonAvatar userId={account.userId} name={account.displayName} />
          <div className="min-w-0">
            <p className="app-title truncate text-sm font-semibold">{account.displayName}</p>
            <p className="app-text-muted break-all text-xs">{account.email}</p>
          </div>
        </div>
      }
      renderTrigger={(trigger) => (
        <button {...trigger} type="button" className={classNames("app-topbar-avatar focus:outline-none focus:ring-2 focus:ring-white", glow && "app-connector-glow")}
          aria-label={t("shell.account.open", { name: account.displayName })} title={account.displayName}>
          <PersonAvatar userId={account.userId} name={account.displayName} />
        </button>
      )}
    />
  );
}
